'use client';

import classNames from 'classnames';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import { IconComponent } from '@alltrails/denali/components/Icon';
import IconRenderer from '@alltrails/denali/components/IconRenderer';
import PaidFeatureBadge from '@alltrails/denali/components/SubscriptionBadge/PaidFeatureBadge';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import River from '@alltrails/denali/icons/River';
import Snowflake from '@alltrails/denali/icons/Snowflake';
import TrailGravel from '@alltrails/denali/icons/TrailGravel';
import TrailIcy from '@alltrails/denali/icons/TrailIcy';
import TrailMuddy from '@alltrails/denali/icons/TrailMuddy';
import TrailPartiallyPaved from '@alltrails/denali/icons/TrailPartiallyPaved';
import Uv from '@alltrails/denali/icons/Uv';
import Waves from '@alltrails/denali/icons/Waves';
import Hurricane from '@alltrails/denali/icons/Weather1';
import Wind from '@alltrails/denali/icons/Wind';
import { ConditionStyleId } from '@alltrails/maps/types/Styles';
import TrailConditionKey from '../../types/TrailConditionKey';
import TrailWeatherConditionsData from '../../types/TrailWeatherConditionsData';
import TrailWeatherOverviewBackgroundTheme from '../../types/TrailWeatherOverviewBackgroundTheme';
import ConditionsErrorCard from '../ConditionsErrorCard/ConditionsErrorCard';
import ConditionsWeatherMapButton from '../ConditionsWeatherMapButton/ConditionsWeatherMapButton';
import styles from './ConditionsCard.module.scss';

type ConditionsCardProps = {
  /**
   * When true, forces the compact (mobile-style) layout regardless of viewport.
   */
  compact?: boolean;
  data?: TrailWeatherConditionsData;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
  onTerrainClick: (condition: ConditionStyleId) => void;
  onWeatherMapClick: (condition: ConditionStyleId) => void;
  theme?: TrailWeatherOverviewBackgroundTheme;
  trailId?: number;
};

const ConditionsCard = ({ compact, data, isError, isFetching, onRetry, onTerrainClick, onWeatherMapClick, theme, trailId }: ConditionsCardProps) => {
  const screenIsMobile = useIsMobileSizedScreen();
  const isMobile = compact ?? screenIsMobile;

  const renderIcon = useCallback(
    (Component: IconComponent) => {
      return <IconRenderer defaults={{ color: 'currentColor', size: isMobile ? 'sm' : 'md' }} icon={{ Component }} />;
    },
    [isMobile]
  );

  if (isError) {
    return <ConditionsErrorCard className={styles.errorCard} onRetry={onRetry} showIllustration trailId={trailId} />;
  }
  if (isFetching || !data || !theme) {
    return <ShimmerContainer className={classNames(styles.container, styles.loading)} />;
  }

  const renderTerrainIcon = () => {
    let icon = TrailPartiallyPaved;
    switch (data.terrain.conditionKey) {
      case 'dry':
      case 'dry_dusty':
        icon = TrailGravel;
        break;
      case 'icy':
        icon = TrailIcy;
        break;
      case 'muddy':
      case 'muddy_icy':
        icon = TrailMuddy;
        break;
      case 'snowy':
      case 'snowy_icy':
        icon = Snowflake;
        break;
      case 'wet':
        icon = River;
        break;
      default:
        break;
    }
    return renderIcon(icon);
  };

  const renderWindIcon = () => {
    let icon = Wind;
    switch (data.wind.conditionKey) {
      case 'calm':
      case 'light_air':
      case 'light_breeze':
      case 'gentle_breeze':
        icon = Waves;
        break;
      case 'hurricane_force':
        icon = Hurricane;
        break;
      default:
        break;
    }
    return renderIcon(icon);
  };

  const weatherCondition: ConditionStyleId = theme.includes('rainy') ? 'precipitation' : 'temperature';

  const terrainCondition: ConditionStyleId = (['snowy', 'snowy_icy', 'icy', 'muddy_icy'] as TrailConditionKey[]).includes(data.terrain.conditionKey)
    ? 'snowDepth'
    : 'groundConditions';

  const terrainDataItem = (
    <button className={styles.dataItem} onClick={() => onTerrainClick(terrainCondition)}>
      {renderTerrainIcon()}
      <div className={styles.dataText}>
        <div className={styles.title}>
          {data.terrain.title}
          <ChevronSm color="currentColor" orientation="right" size={isMobile ? 'sm' : 'md'} />
        </div>
        <div className={styles.description}>{data.terrain.description}</div>
      </div>
    </button>
  );

  return (
    <div className={classNames(styles.container, styles[theme])}>
      <div className={styles.mainContent}>
        <div className={styles.heading}>
          <FormattedMessage defaultMessage="Trail conditions" />
          <PaidFeatureBadge featureTier="plus" size={isMobile ? 'sm' : 'md'} textColor={theme.includes('night') ? 'light' : 'dark'} variant="full" />
        </div>
        <div className={styles.data}>
          {terrainDataItem}
          <div className={styles.dataItem}>
            {renderWindIcon()}
            <div className={styles.dataText}>
              <div className={styles.title}>{data.wind.title}</div>
              <div className={styles.description}>{data.wind.description}</div>
            </div>
          </div>
          <div className={styles.dataItem}>
            {renderIcon(Uv)}
            <div className={styles.dataText}>
              <div className={styles.title}>{data.uv.title}</div>
              <div className={styles.description}>{data.uv.description}</div>
            </div>
          </div>
        </div>
      </div>
      <ConditionsWeatherMapButton condition={weatherCondition} onClick={() => onWeatherMapClick(weatherCondition)} />
    </div>
  );
};

export default ConditionsCard;
